import React, { Fragment, useState } from 'react';
import { API_ETH_MOCK_ADDRESS } from '@yldr/contract-helpers';
import { Trans } from '@lingui/macro';
import { Box, Typography, useMediaQuery, useTheme } from '@mui/material';
import { ListColumn } from 'src/components/lists/ListColumn';
import { ListHeaderTitle } from 'src/components/lists/ListHeaderTitle';
import { ListHeaderWrapper } from 'src/components/lists/ListHeaderWrapper';
import { AssetCapsProvider } from 'src/hooks/useAssetCaps';
import { useProtocolDataContext } from 'src/hooks/useProtocolDataContext';
import { fetchIconSymbolAndName } from 'src/ui-config/reservePatches';

import { EDisplayMode, ListWrapper } from '../../../../components/lists/ListWrapper';
import { useAppDataContext } from '../../../../hooks/app-data-provider/useAppDataProvider';
import {
  DASHBOARD_LIST_COLUMN_WIDTHS,
  DashboardReserve,
  handleSortDashboardReserves,
} from '../../../../utils/dashboardSortUtils';
import { SuppliedPositionsListMobileItem } from './SuppliedPositionsListMobileItem';
import { PositionsList } from '../../../../components/PositionsList/PositionsList';
import { useRootStore } from '../../../../store/root';
import { selectEnrichedUniswapPositions } from '../../../../store/positionSelectors';
import { SuppliedActions } from '../../../../components/PositionsList/components/UniswapPositionsActions/SuppliedActions';
import { ValueInformer } from '../../../../components-yldr/ValueInformer';
import { useSuppliedPositionsDataQuery } from '../../../../hooks/yldr/useSuppliedPositionsDataQuery';
import { DashboardContentNoData } from '../../DashboardContentNoData';
import { ListButtonsColumn } from '../ListButtonsColumn';
import { ListLoader } from '../ListLoader';
import { SuppliedPositionsListItem } from './SuppliedPositionsListItem';

const head = [
  {
    title: <Trans>Asset</Trans>,
    sortKey: 'symbol',
  },
  {
    title: <Trans key="Balance">Balance</Trans>,
    sortKey: 'underlyingBalance',
  },

  {
    title: <Trans key="APY">APY</Trans>,
    sortKey: 'supplyAPY',
  },
];

const positionsHead = [
  {
    title: <Trans>Pool</Trans>,
    sortKey: 'pool',
  },
  {
    title: <Trans key="Liquidity">Liquidity</Trans>,
    sortKey: 'liquidity',
  },
  {
    title: <Trans key="feesEarned">Fees Earned</Trans>,
    sortKey: 'feesEarned',
  },
];

export const SuppliedPositionsList = () => {
  const { user, loading } = useAppDataContext();
  const { currentNetworkConfig } = useProtocolDataContext();
  const { breakpoints } = useTheme();
  const downToXSM = useMediaQuery(breakpoints.down('xsm'));
  const md = useMediaQuery(breakpoints.up('md'));

  const [sortName, setSortName] = useState('');
  const [sortDesc, setSortDesc] = useState(false);
  const [displayMode, setDisplayMode] = useState(EDisplayMode.Assets);

  const { data: positionsData } = useSuppliedPositionsDataQuery();

  const uniswapPositionsData = useRootStore((state) => selectEnrichedUniswapPositions(state, positionsData));

  const suppliedPositions =
    user?.userReservesData
      .filter((userReserve) => userReserve.underlyingBalance !== '0')
      .map((userReserve) => ({
        ...userReserve,
        supplyAPY: userReserve.reserve.supplyAPY, // Note: added only for table sort
        reserve: {
          ...userReserve.reserve,
          ...(userReserve.reserve.isWrappedBaseAsset
            ? fetchIconSymbolAndName({
                symbol: currentNetworkConfig.baseAssetSymbol,
                underlyingAsset: API_ETH_MOCK_ADDRESS.toLowerCase(),
              })
            : {}),
        },
      })) || [];

  const hasAnyReserves = !!suppliedPositions.length || !!uniswapPositionsData.length;

  // Transform to the DashboardReserve schema so the sort helpers can work with it
  const preSortedReserves = suppliedPositions as DashboardReserve[];
  const sortedReserves = handleSortDashboardReserves(
    sortDesc,
    sortName,
    'position',
    preSortedReserves
  );

  if (loading) {
    return <ListLoader title={<Trans>Your supplies</Trans>} head={head.map((col) => col.title)}/>;
  }

  return (
    <ListWrapper
      titleComponent={
        <Typography component="div" variant="h3" sx={{ mr: 4 }}>
          <Trans>Your supplies</Trans>
        </Typography>
      }
      localStorageName="suppliedAssetsDashboardTableCollapse"
      noData={!hasAnyReserves}
      displayMode={displayMode}
      onSwitchDisplayMode={setDisplayMode}
      hasCollapseButton={!md}
      subTitleComponent={(
        <Box sx={{ display: 'flex', gap: 6 }}>
          <ValueInformer
            caption="Total Balance"
            value={user?.totalLiquidityUSD || 0}
            symbol="USD"
          />
          <ValueInformer
            caption="Collateral"
            value={user?.totalCollateralUSD || 0}
            symbol="USD"
          />
          <ValueInformer
            caption="Avg. APY"
            value={user?.earnedAPY || 0}
            percent
          />
        </Box>
      )}
    >
      {displayMode === EDisplayMode.Assets && (
        <>
          {!!sortedReserves.length && !downToXSM && (
            <ListHeaderWrapper>
              {head.map((col) => (
                <ListColumn
                  isRow={col.sortKey === 'symbol'}
                  maxWidth={col.sortKey === 'symbol' ? DASHBOARD_LIST_COLUMN_WIDTHS.ASSET : undefined}
                  key={col.sortKey}
                >
                  <ListHeaderTitle
                    sortName={sortName}
                    sortDesc={sortDesc}
                    setSortName={setSortName}
                    setSortDesc={setSortDesc}
                    sortKey={col.sortKey}
                  >
                    {col.title}
                  </ListHeaderTitle>
                </ListColumn>
              ))}
              <ListButtonsColumn isColumnHeader />
            </ListHeaderWrapper>
          )}
          {sortedReserves.length ? sortedReserves.map((item) => (
            <Fragment key={item.underlyingAsset}>
              <AssetCapsProvider asset={item.reserve}>
                {downToXSM ? (
                  <SuppliedPositionsListMobileItem {...item} />
                ) : (
                  <SuppliedPositionsListItem {...item} />
                )}
              </AssetCapsProvider>
            </Fragment>
          )) : (
            <DashboardContentNoData text={<Trans>Nothing supplied yet</Trans>} />
          )}
        </>
      )}
      {displayMode === EDisplayMode.Positions && (
        <PositionsList
          head={positionsHead}
          positions={uniswapPositionsData}
          actions={(args) => <SuppliedActions tokenId={args.tokenId} />}
          columnsType='lending'
        />
      )}
    </ListWrapper>
  );
};
